import React from 'react';
import { Card, InputNumber, Input, Button, Select, Tag, Dropdown } from 'antd';
import { PlusOutlined, DeleteOutlined, CloseOutlined, PlusCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import type { FabricConfig, FabricType, StageType, ChainStage, YarnEntry, FabricResult } from './types';
import { STAGE_LABELS, KNIT_KG_STAGES } from './types';

/** 過濾非數字字元（允許小數點） */
const numParser = (val: string | undefined) => Number(val?.replace(/[^\d.]/g, '') || 0);

let _id = 100;
const nextId = () => `s${++_id}`;

/** 新增工段時的預設值 */
const DEFAULTS: Record<FabricType, Record<StageType, Omit<ChainStage, 'id' | 'type'>>> = {
  woven: {
    knitting:    { cost: 0,    lossRate: 0,  inspectionFee: 0 },
    dyeing:      { cost: 16,   lossRate: 18, inspectionFee: 0 },
    chemical:    { cost: 11.5, lossRate: 0,  inspectionFee: 0 },
    brushing:    { cost: 8,    lossRate: 2,  inspectionFee: 1.5 },
    inspection:  { cost: 1.5,  lossRate: 0,  inspectionFee: 0 },
    printing:    { cost: 25,   lossRate: 3,  inspectionFee: 3.7 },
    calendering: { cost: 12,   lossRate: 2,  inspectionFee: 1.2 },
    lamination:  { cost: 40,   lossRate: 5,  inspectionFee: 1.5 },
  },
  knit: {
    knitting:    { cost: 20,   lossRate: 1,  inspectionFee: 0 },
    dyeing:      { cost: 65,   lossRate: 5,  inspectionFee: 0 },
    chemical:    { cost: 10,   lossRate: 5,  inspectionFee: 0 },
    brushing:    { cost: 50,   lossRate: 3,  inspectionFee: 0 },
    inspection:  { cost: 0,    lossRate: 0,  inspectionFee: 0 }, // KNIT: 費用由公式自動算
    printing:    { cost: 30,   lossRate: 3,  inspectionFee: 3.7 },
    calendering: { cost: 15,   lossRate: 2,  inspectionFee: 1.5 },
    lamination:  { cost: 40,   lossRate: 5,  inspectionFee: 1.5 },
  },
};

/** 每個工段的供應商預設組合 */
interface SupplierPreset {
  label: string;
  cost: number;
  lossRate: number;
  inspectionFee: number;
}

const SUPPLIERS: Record<FabricType, Partial<Record<StageType, SupplierPreset[]>>> = {
  woven: {
    dyeing: [
      { label: '預設',    cost: 16, lossRate: 18, inspectionFee: 0 },
      { label: '供應商B', cost: 18, lossRate: 15, inspectionFee: 0 },
      { label: '供應商C', cost: 14, lossRate: 20, inspectionFee: 0 },
    ],
    chemical: [
      { label: '預設',    cost: 11.5, lossRate: 0, inspectionFee: 0 },
      { label: '供應商B', cost: 13,   lossRate: 0, inspectionFee: 0 },
    ],
    brushing: [
      { label: '預設',    cost: 8,  lossRate: 2, inspectionFee: 1.5 },
      { label: '供應商B', cost: 10, lossRate: 3, inspectionFee: 1.5 },
    ],
    inspection: [
      { label: '預設 1.5/YD', cost: 1.5, lossRate: 0, inspectionFee: 0 },
      { label: '2.0/YD',      cost: 2.0, lossRate: 0, inspectionFee: 0 },
    ],
    printing: [
      { label: '預設',    cost: 25, lossRate: 3, inspectionFee: 3.7 },
      { label: '供應商B', cost: 30, lossRate: 2, inspectionFee: 3.7 },
    ],
    calendering: [
      { label: '預設',    cost: 12, lossRate: 2, inspectionFee: 1.2 },
      { label: '供應商B', cost: 15, lossRate: 2, inspectionFee: 1.2 },
    ],
    lamination: [
      { label: '2L 貼合',   cost: 40, lossRate: 5, inspectionFee: 1.5 },
      { label: '2.5L 貼合', cost: 45, lossRate: 5, inspectionFee: 1.5 },
      { label: '3L 貼合',   cost: 55, lossRate: 5, inspectionFee: 1.5 },
    ],
  },
  knit: {
    knitting: [
      { label: '預設',    cost: 20, lossRate: 1, inspectionFee: 0 },
      { label: '供應商B', cost: 25, lossRate: 1, inspectionFee: 0 },
    ],
    dyeing: [
      { label: '預設',    cost: 65, lossRate: 5, inspectionFee: 0 },
      { label: '供應商B', cost: 70, lossRate: 4, inspectionFee: 0 },
    ],
    chemical: [
      { label: '預設',    cost: 10, lossRate: 5, inspectionFee: 0 },
      { label: '供應商B', cost: 12, lossRate: 3, inspectionFee: 0 },
    ],
    brushing: [
      { label: '預設',    cost: 50, lossRate: 3, inspectionFee: 0 },
      { label: '供應商B', cost: 55, lossRate: 2, inspectionFee: 0 },
    ],
    inspection: [], // KNIT 驗貨費由碼重自動計算，無供應商選項
    printing: [
      { label: '預設',    cost: 30, lossRate: 3, inspectionFee: 3.7 },
      { label: '供應商B', cost: 35, lossRate: 2, inspectionFee: 3.7 },
    ],
    calendering: [
      { label: '預設',    cost: 15, lossRate: 2, inspectionFee: 1.5 },
      { label: '供應商B', cost: 18, lossRate: 2, inspectionFee: 1.5 },
    ],
    lamination: [
      { label: '2L 貼合',   cost: 40, lossRate: 5, inspectionFee: 1.5 },
      { label: '2.5L 貼合', cost: 45, lossRate: 5, inspectionFee: 1.5 },
      { label: '3L 貼合',   cost: 55, lossRate: 5, inspectionFee: 1.5 },
    ],
  },
};

const WOVEN_MENU: StageType[] = ['dyeing', 'chemical', 'brushing', 'inspection', 'printing', 'calendering', 'lamination'];
const KNIT_ALL_MENU: StageType[] = ['knitting', 'dyeing', 'chemical', 'brushing', 'inspection', 'printing', 'calendering', 'lamination'];

interface Props {
  config: FabricConfig;
  result: FabricResult;
  onChange: (update: FabricConfig | ((prev: FabricConfig) => FabricConfig)) => void;
  label?: string;
  className?: string;
}

const FabricChain: React.FC<Props> = ({ config, result, onChange, label, className }) => {
  const isKnit = config.type === 'knit';

  // 全部用 functional update，避免閉包拿到過期 state
  const update = (patch: Partial<FabricConfig>) =>
    onChange(prev => ({ ...prev, ...patch }));

  const updateStage = (id: string, patch: Partial<ChainStage>) =>
    onChange(prev => ({
      ...prev,
      stages: prev.stages.map(s => (s.id === id ? { ...s, ...patch } : s)),
    }));

  const addStage = (type: StageType, insertAfterIndex?: number) => {
    const d = DEFAULTS[config.type][type];
    const newStage = { id: nextId(), type, ...d };
    onChange(prev => {
      if (insertAfterIndex !== undefined) {
        const arr = [...prev.stages];
        arr.splice(insertAfterIndex + 1, 0, newStage);
        return { ...prev, stages: arr };
      }
      return { ...prev, stages: [...prev.stages, newStage] };
    });
  };

  const removeStage = (id: string) =>
    onChange(prev => ({ ...prev, stages: prev.stages.filter(s => s.id !== id) }));

  const moveStage = (stageId: string, direction: 'up' | 'down') => {
    onChange(prev => {
      const stages = [...prev.stages];
      const idx = stages.findIndex(s => s.id === stageId);
      if (idx < 0) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === stages.length - 1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      [stages[idx], stages[targetIdx]] = [stages[targetIdx], stages[idx]];
      return { ...prev, stages };
    });
  };

  // Yarn
  const updateYarn = (id: string, field: keyof YarnEntry, v: string | number) =>
    onChange(prev => ({
      ...prev,
      yarns: prev.yarns.map(y => (y.id === id ? { ...y, [field]: v } : y)),
    }));

  const addYarn = () =>
    onChange(prev => ({
      ...prev,
      yarns: [...prev.yarns, { id: nextId(), code: '', price: 0, ratio: 0 }],
    }));

  const removeYarn = (id: string) =>
    onChange(prev => ({ ...prev, yarns: prev.yarns.filter(y => y.id !== id) }));

  // Stage result lookup
  const sr = (id: string) => result.stages.find(s => s.id === id);

  // WOVEN 直接用全部 stages；KNIT 不再分組
  const allStages = config.stages;

  // Dropdown menus
  const makeMenu = (types: StageType[]) => ({
    items: types.map(t => ({
      key: t,
      label: isKnit
        ? `${STAGE_LABELS[t]}${KNIT_KG_STAGES.includes(t) ? ' (KG)' : ' (YD)'}`
        : STAGE_LABELS[t],
      onClick: () => addStage(t),
    })),
  });

  // ---- KNIT 驗貨自動計算費用 ----
  const knitInspCost = () => {
    const kgPerYd = config.fabricWeight / 1000;
    return config.fabricWeight >= 250 ? 5 * kgPerYd : 1.5;
  };

  // ---- Render a stage card ----
  const renderStage = (stage: ChainStage, stageIndex: number) => {
    const r = sr(stage.id);
    const isChem = stage.type === 'chemical';
    const isInsp = stage.type === 'inspection';
    const isKnitInsp = isKnit && isInsp;
    const presets = SUPPLIERS[config.type][stage.type];
    const menuTypes = isKnit ? KNIT_ALL_MENU : WOVEN_MENU;
    const insertMenu = {
      items: menuTypes.map(t => ({
        key: t,
        label: isKnit
          ? `${STAGE_LABELS[t]}${KNIT_KG_STAGES.includes(t) ? ' (KG)' : ' (YD)'}`
          : STAGE_LABELS[t],
        onClick: () => addStage(t, stageIndex),
      })),
    };

    const applyPreset = (idx: number) => {
      if (!presets || !presets.length) return;
      const p = presets[idx];
      if (p) updateStage(stage.id, { cost: p.cost, lossRate: p.lossRate, inspectionFee: p.inspectionFee });
    };

    return (
      <React.Fragment key={stage.id}>
      <Card size="small" className="mb-1" style={{ backgroundColor: '#dcfce7', borderColor: '#86efac' }}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1">
            <span className="font-medium text-sm">{STAGE_LABELS[stage.type]}</span>
            {isKnit && (
              <Tag className="text-xs" color={KNIT_KG_STAGES.includes(stage.type) ? 'default' : 'green'}>
                {KNIT_KG_STAGES.includes(stage.type) ? 'KG' : 'YD'}
              </Tag>
            )}
          </div>
          <div className="flex items-center gap-1">
            {r && r.lossAmount > 0 && <Tag color="orange" className="text-xs">損耗 +{r.lossAmount.toFixed(2)}</Tag>}
            {r && (
              <Tag color="blue" className="text-xs">
                累計 {r.subtotal.toFixed(2)}{' '}
                {isKnit ? (KNIT_KG_STAGES.includes(stage.type) ? 'KG' : 'YD') : 'YD'}
              </Tag>
            )}
            <Button size="small" type="text" icon={<ArrowUpOutlined />}
              disabled={stageIndex === 0}
              onClick={() => moveStage(stage.id, 'up')}
              style={{ padding: '0 4px' }} />
            <Button size="small" type="text" icon={<ArrowDownOutlined />}
              disabled={stageIndex === config.stages.length - 1}
              onClick={() => moveStage(stage.id, 'down')}
              style={{ padding: '0 4px' }} />
            <Button size="small" type="text" danger icon={<CloseOutlined />} onClick={() => removeStage(stage.id)} />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          {presets && presets.length > 0 && (
            <div>
              <div className="text-xs text-gray-500">供應商</div>
              <Select
                size="small"
                style={{ width: 110 }}
                placeholder="選擇"
                onChange={applyPreset}
                options={presets.map((p, i) => ({ label: p.label, value: i }))}
              />
            </div>
          )}
          {isChem && (
            <div>
              <div className="text-xs text-gray-500">藥劑產品</div>
              <Input size="small" style={{ width: 110 }} placeholder="產品名稱"
                value={stage.chemicalProduct || ''}
                onChange={e => updateStage(stage.id, { chemicalProduct: e.target.value })} />
            </div>
          )}
          {isKnitInsp ? (
            /* KNIT 驗貨：費用自動計算，不可編輯 */
            <div>
              <div className="text-xs text-gray-500">
                {config.fabricWeight >= 250
                  ? `5元/KG × ${(config.fabricWeight / 1000).toFixed(3)} kg/yd`
                  : '固定 1.5 元/YD (碼重 < 250g)'}
              </div>
              <InputNumber size="small" parser={numParser} value={Number(knitInspCost().toFixed(2))} disabled style={{ width: 100 }}
                addonAfter="/YD" />
            </div>
          ) : isInsp ? (
            /* WOVEN 驗貨：可編輯 */
            <div>
              <div className="text-xs text-gray-500">費用 (/YD)</div>
              <InputNumber size="small" parser={numParser} value={stage.cost} min={0} step={0.1} style={{ width: 85 }}
                onChange={v => updateStage(stage.id, { cost: v ?? 0 })} />
            </div>
          ) : (
            /* 一般工段 */
            <>
              <div>
                <div className="text-xs text-gray-500">費用</div>
                <InputNumber size="small" parser={numParser} value={stage.cost} min={0} step={0.5} style={{ width: 85 }}
                  onChange={v => updateStage(stage.id, { cost: v ?? 0 })} />
              </div>
              <div>
                <div className="text-xs text-gray-500">{isChem ? '自身損耗%' : '損耗率%'}</div>
                <InputNumber size="small" parser={numParser} value={stage.lossRate} min={0} max={99} step={0.5} style={{ width: 80 }}
                  onChange={v => updateStage(stage.id, { lossRate: v ?? 0 })} />
              </div>
              {!isChem && (
                <div>
                  <div className="text-xs text-gray-500">驗貨費</div>
                  <InputNumber size="small" parser={numParser} value={stage.inspectionFee} min={0} step={0.1} style={{ width: 75 }}
                    onChange={v => updateStage(stage.id, { inspectionFee: v ?? 0 })} />
                </div>
              )}
            </>
          )}
        </div>
      </Card>
      <Dropdown menu={insertMenu} trigger={['click']}>
        <div
          style={{ display: 'flex', alignItems: 'center', height: 20, marginBottom: 4, cursor: 'pointer' }}
          className="group"
        >
          <div style={{ flex: 1, height: 1, borderTop: '1px dashed #d1d5db' }} />
          <span style={{ fontSize: 11, color: '#9ca3af', padding: '0 6px', whiteSpace: 'nowrap' }}
            className="group-hover:text-green-600">
            <PlusCircleOutlined style={{ fontSize: 11, marginRight: 3 }} />插入工段
          </span>
          <div style={{ flex: 1, height: 1, borderTop: '1px dashed #d1d5db' }} />
        </div>
      </Dropdown>
      </React.Fragment>
    );
  };

  // ---- Main Render ----
  return (
    <div className={`min-w-0 ${className || ''}`}>
      {/* Header */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        {label && <span className="font-bold">{label}</span>}
        <Select
          size="small"
          value={config.type}
          onChange={(v: FabricType) => update({
            type: v,
            stages: [],
            yarns: v === 'knit' ? [{ id: nextId(), code: '', price: 0, ratio: 0 }] : [],
          })}
          options={[
            { label: '梭織 WOVEN', value: 'woven' },
            { label: '針織 KNIT', value: 'knit' },
          ]}
          style={{ width: 130 }}
        />
        <Input size="small" placeholder="布號" value={config.fabricCode} style={{ width: 150 }}
          onChange={e => update({ fabricCode: e.target.value })} />
      </div>

      {isKnit ? (
        /* ========== KNIT ========== */
        <>
          {/* 紗線配比 + 碼重 */}
          <Card size="small" className="mb-2" title={
            <div className="flex justify-between items-center text-sm">
              <span>紗線配比</span>
              <div className="flex gap-2 items-center">
                <span className="text-gray-500">碼重:</span>
                <InputNumber size="small" parser={numParser} value={config.fabricWeight} min={1} step={10} style={{ width: 75 }}
                  onChange={v => update({ fabricWeight: v ?? 1 })} />
                <span className="text-gray-400 text-xs">g/yd</span>
                {result.stages[0] && <Tag color="blue" className="text-xs">{result.stages[0].subtotal.toFixed(2)} KG</Tag>}
              </div>
            </div>
          }>
            {config.yarns.map(y => (
              <div key={y.id} className="flex gap-2 items-center mb-1">
                <Input size="small" style={{ width: 85 }} placeholder="紗代號" value={y.code}
                  onChange={e => updateYarn(y.id, 'code', e.target.value)} />
                <InputNumber size="small" parser={numParser} value={y.price} min={0} style={{ width: 65 }} placeholder="紗價"
                  onChange={v => updateYarn(y.id, 'price', v ?? 0)} />
                <InputNumber size="small" parser={numParser} value={y.ratio} min={0} max={100} step={0.1} style={{ width: 65 }}
                  onChange={v => updateYarn(y.id, 'ratio', v ?? 0)} />
                <span className="text-xs text-gray-400 whitespace-nowrap">% = {(y.price * y.ratio / 100).toFixed(1)}</span>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeYarn(y.id)} />
              </div>
            ))}
            {(() => {
              const totalRatio = config.yarns.reduce((sum, y) => sum + (y.ratio || 0), 0);
              const over = totalRatio > 100;
              return (
                <div className="flex items-center gap-2 mb-2 mt-1">
                  <span style={{
                    fontSize: 12, fontWeight: 'bold', padding: '1px 8px', borderRadius: 4,
                    backgroundColor: over ? '#fee2e2' : totalRatio === 100 ? '#bbf7d0' : '#fef9c3',
                    color: over ? '#dc2626' : totalRatio === 100 ? '#16a34a' : '#a16207',
                    border: `1px solid ${over ? '#fca5a5' : totalRatio === 100 ? '#86efac' : '#fde68a'}`,
                  }}>
                    配比合計: {totalRatio.toFixed(1)}%{over && ' ⚠'}
                  </span>
                  {over && <span style={{ fontSize: 11, color: '#dc2626' }}>超過 100%，請重新確認</span>}
                  {!over && totalRatio > 0 && totalRatio < 100 && (
                    <span style={{ fontSize: 11, color: '#a16207' }}>剩餘 {(100 - totalRatio).toFixed(1)}%</span>
                  )}
                </div>
              );
            })()}
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addYarn}>新增紗線/成品布</Button>
          </Card>

          {/* 工段依加入順序顯示，KG→YD 轉換自動插在第一個 YD 工段之前 */}
          {(() => {
            const els: React.ReactNode[] = [];
            let convShown = false;

            config.stages.forEach((stage, idx) => {
              const isYd = !KNIT_KG_STAGES.includes(stage.type);

              if (isYd && !convShown && result.conversion) {
                els.push(
                  <div key="conv" className="bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2 text-xs flex justify-between items-center">
                    <span className="font-medium">KG → YD ({config.fabricWeight} g/yd)</span>
                    <span>
                      {result.conversion.costPerKg.toFixed(2)} TWD/KG × {(config.fabricWeight / 1000).toFixed(3)}
                      {' = '}<strong>{result.conversion.subtotal.toFixed(2)}</strong> TWD/YD
                    </span>
                  </div>,
                );
                convShown = true;
              }

              els.push(renderStage(stage, idx));
            });

            // 全部都是 KG 工段，轉換列放最後
            if (!convShown && result.conversion) {
              els.push(
                <div key="conv" className="bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2 text-xs flex justify-between items-center">
                  <span className="font-medium">KG → YD ({config.fabricWeight} g/yd)</span>
                  <span>
                    {result.conversion.costPerKg.toFixed(2)} TWD/KG × {(config.fabricWeight / 1000).toFixed(3)}
                    {' = '}<strong>{result.conversion.subtotal.toFixed(2)}</strong> TWD/YD
                  </span>
                </div>,
              );
            }

            return els;
          })()}

          <Dropdown menu={makeMenu(KNIT_ALL_MENU)} trigger={['click']}>
            <Button size="small" type="dashed" icon={<PlusOutlined />} className="w-full">
              新增工段
            </Button>
          </Dropdown>
        </>
      ) : (
        /* ========== WOVEN ========== */
        <>
          {/* 胚布 */}
          <Card size="small" className="mb-2" title={
            <div className="flex justify-between items-center text-sm">
              <span>胚布</span>
              {result.stages[0] && <Tag color="blue" className="text-xs">小計 {result.stages[0].subtotal.toFixed(2)} YD</Tag>}
            </div>
          }>
            <div className="flex gap-3 items-end">
              <div>
                <div className="text-xs text-gray-500">胚布編號</div>
                <Input size="small" style={{ width: 100 }} placeholder="編號" value={config.rawFabricCode}
                  onChange={e => update({ rawFabricCode: e.target.value })} />
              </div>
              <div>
                <div className="text-xs text-gray-500">胚價</div>
                <InputNumber size="small" parser={numParser} value={config.basePrice} min={0} style={{ width: 80 }}
                  onChange={v => update({ basePrice: v ?? 0 })} />
              </div>
              <div>
                <div className="text-xs text-gray-500">損耗%</div>
                <InputNumber size="small" parser={numParser} value={config.baseLossRate} min={0} max={99} step={0.5} style={{ width: 70 }}
                  onChange={v => update({ baseLossRate: v ?? 0 })} />
              </div>
              <div>
                <div className="text-xs text-gray-500">加工費</div>
                <InputNumber size="small" parser={numParser} value={config.processingFee} min={0} style={{ width: 80 }}
                  onChange={v => update({ processingFee: v ?? 0 })} />
              </div>
            </div>
          </Card>

          {/* 工段 */}
          {allStages.map(renderStage)}

          <Dropdown menu={makeMenu(WOVEN_MENU)} trigger={['click']}>
            <Button size="small" type="dashed" icon={<PlusOutlined />} className="w-full">
              新增工段
            </Button>
          </Dropdown>
        </>
      )}
    </div>
  );
};

export default FabricChain;
